import React from 'react';
import Box from 'components/Box';
import BoxBody from 'components/BoxBody';
import Button from 'components/Button';
import Container from 'components/Container';
import Divider from 'components/Divider';
import Link from 'components/Link';
import Row from 'components/Row';
import Text from 'components/Text';
import CoachingConstants from 'coaching/CoachingConstants';
import CoachingHelpSectionHeader from 'coaching/components/CoachingHelpFormView/CoachingHelpSectionHeader';
// @ts-ignore
import userAgent from 'lib/currentUserAgent';

import 'coaching/components/styles/CoachingHelpContactUsSection.scss';

export default function ContactUsSection() {
  const { CONTACT_REASON_LINKS } = CoachingConstants;
  return (
    <Container>
      <CoachingHelpSectionHeader
        icon="mail"
        headerText="Contact Us"
        detailText={`Choose an option that fits your request. ${CoachingConstants.COACHING_HELP_HOURS}`}
      />
      {userAgent.isDesktop() ? (
        <div className="CoachingHelpContactUsSection-cards">
          {CONTACT_REASON_LINKS.map(({ subject, links, buttons }) => (
            <ContactSubjectCard key={subject} subject={subject} links={links} buttons={buttons} />
          ))}
        </div>
      ) : (
        <React.Fragment>
          {CONTACT_REASON_LINKS.map(({ subject, links, buttons }, i) => (
            <ContactSubjectMobile
              key={subject}
              buttons={buttons}
              subject={subject}
              links={links}
              divider={i < CONTACT_REASON_LINKS.length - 1}
            />
          ))}
        </React.Fragment>
      )}
    </Container>
  );
}

type CardProps = {
  subject: String;
  links: { label: string; to: string }[];
  buttons?: { description: string; label: string; to: string }[];
};

function ContactSubjectCard(props: CardProps) {
  const { subject, links, buttons } = props;

  return (
    <Box noShadow className="CoachingHelpContactUsSection-cardWrapper" textAlign="left">
      <BoxBody className="CoachingHelpContactUsSection-card">
        <Row size="medium">
          <Text size="xlarge" weight="semibold">
            {subject}
          </Text>
        </Row>
        {links.map(({ label, to }) => (
          <Row key={label} size="small">
            {/* Link uses href to ensure Zendesk chat is unloaded */}
            <Link href={to} color="ink">
              <Text size="medium">{label}</Text>
            </Link>
          </Row>
        ))}
        {buttons &&
          buttons.map(({ description, label, to }) => (
            <ButtonWithDescription description={description} label={label} to={to} />
          ))}
      </BoxBody>
    </Box>
  );
}

type MobileSectionProps = CardProps & { divider: boolean };

function ContactSubjectMobile(props: MobileSectionProps) {
  const { subject, links, divider, buttons } = props;

  return (
    <React.Fragment>
      <Row size="small">
        <Text size="medium" weight="semibold">
          {subject}
        </Text>
      </Row>
      {links.map(({ label, to }) => (
        <Row key={label} size="small">
          <Link to={to} color="ink">
            <Text size="small">{label}</Text>
          </Link>
        </Row>
      ))}
      {buttons &&
        buttons.map(({ description, label, to }) => (
          <ButtonWithDescription description={description} label={label} to={to} />
        ))}
      {divider && <Divider size="large" />}
    </React.Fragment>
  );
}

function ButtonWithDescription({
  description,
  label,
  to,
}: {
  description: string;
  label: string;
  to: string;
}) {
  return (
    <>
      <Row key={description} size="xsmall">
        <Text color="graphite" size={userAgent.isDesktop() ? 'medium' : 'small'}>
          {description}
        </Text>
      </Row>
      <Row key={label} size="small">
        <Button color="whiteout" level="secondary" to={to} size="small">
          {label}
        </Button>
      </Row>
    </>
  );
}
